import { renderedStylesTag } from './extensions/style';

/**
 * A function that recursively checks for a P tag's `firstChil` and appends it to the passed
 * `li` element. When `p.firstChild` is defined, we recursively call this function until `p`
 * is empty. Once done, we remove the element from the parsed dom.
 * @param p
 * @param li
 */
export const appendInnerPContentToOuterLI = (p: Element, li: Element) => {
  if (p.firstChild) {
    li.insertBefore(p.firstChild, p);
    appendInnerPContentToOuterLI(p, li);
  }

  if (!p.firstChild) {
    p.remove();
  }
};

/**
 * Dom parser formatting for LI tags to remove the inner P tag
 * that TipTap adds to list elements.
 *
 * ex: TipTap produces `<li><p>Content</p></li>`. We want to parse
 * this to `<li>Content</li>`, matching what CKEditor 4 persisted.
 *
 * Only the P that TipTap added is unwrapped: the LI's leading P, when everything after it is a
 * nested list (TipTap renders an indented item as `<li><p>One</p><ul>..</ul></li>`). A list item
 * holding several blocks of its own (`<li><h4>Airdrie</h4><p>6 Kingsview Rd SE</p></li>`) was
 * authored that way, so its P tags are left alone to match what CKEditor 4 persisted.
 * @param doc
 */
export const formatLI = (doc: Document) => {
  const listItems = doc.querySelectorAll('li');

  listItems.forEach((li) => {
    const [ firstChild, ...siblings ] = li.children;

    if (firstChild?.tagName !== 'P') {
      return;
    }

    // A leading P is TipTap's wrapper only when the item holds no further content of its own.
    const isWrapperOnly = siblings.every((sibling) => [ 'UL', 'OL' ].includes(sibling.tagName));

    if (!isWrapperOnly) {
      return;
    }

    appendInnerPContentToOuterLI(firstChild, li);
  });
};

/**
 * Remove empty trailing <p> tags in the parsed dom.
 * Also handles cases where the only content is an empty <p> tag
 * @param doc
 */
export const formatEmptyPTag = (doc: Document) => {
  if (
    doc.body.lastChild?.nodeName === 'P'
    && doc.body.lastChild?.textContent?.trim() === ''
  ) {
    doc.body.lastChild?.remove();
  }
};

/**
 * In order to preserve <style> tags in tip tap, we need to render it
 * as a <styles> element, as <style> gets dropped. So when the editor content changes,
 * we replace any `<styles>` tags with
 * @param doc
 */
export const formatStyle = (doc: Document) => {
  const els = [ ...doc.querySelectorAll(renderedStylesTag) ];

  els.forEach((el) => {
    const newEl = doc.createElement('style');
    newEl.type = 'text/css';
    newEl.appendChild(doc.createTextNode(el.textContent ?? ''));
    el.replaceWith(newEl);
  });
};

/**
 * HTML Formatter pass-through for TipTap Content.
 * Currently we parse a string into a dom object then strip or modify any unnecessary elements
 * For example, lists contain a <p> tag that wrecks content so we want to replace that <p> tag with its inner contents.
 * @param html
 * @returns
 */
export const formatHTML = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  formatLI(doc);
  formatEmptyPTag(doc);
  formatStyle(doc);

  return doc.body.innerHTML;
};
